import React from 'react';
import styled from 'styled-components';
import { breakpoints, Container } from '../../styles/layout';
import { colors } from '../../styles/colors';
import { rtypeN18r, rtypeN21bu, typeN16bu } from '../../styles/typography/presets';
import { RichText } from 'prismic-reactjs';
import { linkResolver } from '../../resolvers';
import { Link } from 'gatsby';

const Wrapper = styled.div`
  background: ${colors.lightGrey};
  padding: 60px 0;

  @media(min-width: ${breakpoints.lg}) {
    padding: 90px 0;
  }
`;

const MainHeading = styled.h2`
  ${rtypeN21bu};
  color: ${colors.purple};
  text-align: center;
  margin-bottom: 40px;
  grid-column: 1 / -1;

  @media(min-width: ${breakpoints.lg}) {
    margin-bottom: 60px;
  }
`;

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  grid-column: 2 / span 8;
  position: relative;

  @media(min-width: ${breakpoints.md}) {
    flex-direction: row;
    gap: 0;
  }

  @media(min-width: ${breakpoints.lg}) {
    grid-column: 2 / span 8;
  }
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 20px;

  @media(min-width: ${breakpoints.md}) {
    padding: 0 40px;
  }
`;

const ColumnTitle = styled.h3`
  ${rtypeN18r};
  color: ${colors.darkGrey};
  text-transform: uppercase;
  margin-bottom: 30px;
  font-weight: 400;
`;

const Divider = styled.div`
  display: none;

  @media(min-width: ${breakpoints.md}) {
    display: block;
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: ${colors.grey};
    transform: translateX(-50%);
  }
`;

const StyledButton = styled(Link)`
  ${typeN16bu};
  display: inline-block;
  padding: 12px 40px;
  border-radius: 100px;
  text-decoration: none;
  transition: all 200ms ease-out;
  border: 2px solid;

  ${props => {
    console.log('props', props)
    switch(props.$buttonStyle) {
      case 'Purple':
        return `
          background: ${colors.purple};
          color: ${colors.white};
          border-color: ${colors.purple};

          &:hover {
            background: ${colors.white};
            color: ${colors.purple};
          }
        `;
      case 'White':
        return `
          background: ${colors.white};
          color: ${colors.purple};
          border-color: ${colors.purple};

          &:hover {
            background: ${colors.purple};
            color: ${colors.white};
          }
        `;
      case 'Orange':
      default:
        return `
          background: ${colors.orange};
          color: ${colors.white};
          border-color: ${colors.orange};

          &:hover {
            background: ${colors.white};
            color: ${colors.orange};
          }
        `;
    }
  }}
`;

const FooterContent = styled.div`
  ${rtypeN18r};
  color: ${colors.darkGrey};
  margin-top: 60px;
  grid-column: 2 / span 8;

  p {
    margin-bottom: 1em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
  }

  ul, ol {
    margin: 1em 0;
    padding-left: 2em;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li {
    margin-bottom: 0.5em;
  }

  a {
    color: ${colors.blue};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  @media(min-width: ${breakpoints.lg}) {
    grid-column: 3 / span 6;
  }
`;

const TwoColumnCTA = ({ slice }) => {
  const {
    main_heading,
    left_column_title,
    left_button_text,
    left_button_url,
    left_button_style,
    right_column_title,
    right_button_text,
    right_button_url,
    right_button_style,
    footer_content,
    divider_visible
  } = slice.primary;

  const getButtonUrl = (url) => {
    if (!url) return '#';

    if (url.link_type === 'Web') {
      return url.url || '#';
    } else if (url.link_type === 'Document') {
      return linkResolver(url) || '#';
    }
    return url.url || url || '#';
  };

  return (
    <Wrapper>
      <Container>
        {main_heading && <MainHeading>{main_heading}</MainHeading>}

        <ColumnsContainer>
          {divider_visible && <Divider />}

          <Column>
            {left_column_title && <ColumnTitle>{left_column_title}</ColumnTitle>}
            {left_button_text && (
              <StyledButton
                to={getButtonUrl(left_button_url)}
                $buttonStyle={left_button_style}
              >
                {left_button_text}
              </StyledButton>
            )}
          </Column>

          <Column>
            {right_column_title && <ColumnTitle>{right_column_title}</ColumnTitle>}
            {right_button_text && (
              <StyledButton
                to={getButtonUrl(right_button_url)}
                $buttonStyle={right_button_style}
              >
                {right_button_text}
              </StyledButton>
            )}
          </Column>
        </ColumnsContainer>

        {footer_content && (
          <FooterContent>
            {RichText.render(footer_content, linkResolver)}
          </FooterContent>
        )}
      </Container>
    </Wrapper>
  );
};

export default TwoColumnCTA;
